import classNames from "classnames";
import moment from "moment";
import React, { FC, Fragment, useState } from "react";
import {
  LuArrowDown,
  LuArrowUp,
  LuChevronDown,
  LuChevronRight,
  LuRepeat,
  LuUsers,
} from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import {
  getInitials,
  getInitialsFromFullName,
  showStatus,
} from "../../../constants/constants";
import { WorkModel } from "../../../factories/works/models/work.model";
import { navigateToContact } from "../../contacts/utils/navigateToContacts";
import { getBudgetColor, getTimeBudgetProgress } from "../components/WorkRow";
import { WorkTasksExpandedTable } from "../components/WorkTasksExpandedTable";
import { StatusesDropdown } from "../dropdown/StatusesDropdown";
import { getStatusColor } from "../dropdown/utils";
import { WorkTableSort } from "../types/work-table";
import { getWorkProgress } from "../utils/getWorkProgress";
import { SelectBox } from "./WorkBulkActions";
import {
  allWorksSelected,
  toggleAllWorks,
  toggleSelectedWork,
} from "./worksBoard.utils";

type Column =
  | "name"
  | "role"
  | "type"
  | "contact"
  | "status"
  | "start"
  | "due"
  | "progress"
  | "budget"
  | "assignee";

const COLUMN_TITLES: Record<Column, string> = {
  name: "Work Name",
  role: "Role",
  type: "Work Type",
  contact: "Contact",
  status: "Status",
  start: "Start Date",
  due: "Due Date",
  progress: "Task Progress",
  budget: "Time Budget",
  assignee: "Assignee",
};

const COLUMN_SORT: Partial<Record<Column, WorkTableSort>> = {
  name: WorkTableSort.WorkName,
  role: WorkTableSort.Role,
  type: WorkTableSort.WorkType,
  contact: WorkTableSort.Contact,
  status: WorkTableSort.Status,
  start: WorkTableSort.StartDate,
  due: WorkTableSort.DueDate,
  progress: WorkTableSort.Progress,
  assignee: WorkTableSort.Assignee,
};

const DEFAULT_COLUMNS: Column[] = [
  "name",
  "type",
  "contact",
  "status",
  "start",
  "due",
  "progress",
  "budget",
  "assignee",
];
const QUEUED_COLUMNS: Column[] = [
  "name",
  "role",
  "contact",
  "status",
  "start",
  "due",
  "type",
];

const TH =
  "whitespace-nowrap px-3.5 py-[13px] text-left text-xs font-bold uppercase tracking-[0.4px]";
const TD = "px-3.5 py-[13px] text-[13.5px] font-semibold text-[#3A4256]";

const Bar: FC<{
  percent: number;
  color: string;
  label: string;
  labelColor?: string;
}> = ({ percent, color, label, labelColor }) => (
  <span className={"flex min-w-[110px] items-center gap-2"}>
    <span
      className={"min-w-[34px] text-[13px] font-semibold"}
      style={{ color: labelColor || "#3A4256" }}>
      {label}
    </span>
    <span
      className={
        "inline-block h-[5px] flex-1 overflow-hidden rounded-[10px] bg-[#E6E8ED]"
      }>
      <span
        className={"block h-[5px] rounded-[10px]"}
        style={{
          width: `${Math.min(100, Math.max(0, percent))}%`,
          backgroundColor: color,
        }}
      />
    </span>
  </span>
);

const subtasksText = (work: WorkModel) => {
  const left = (work.taskCounts || 0) - (work.completedTaskCounts || 0);
  if (!work.taskCounts) return "No subtasks yet";
  if (left <= 0) return "All subtasks complete";
  return `${left} ${left === 1 ? "subtask" : "subtasks"} to do`;
};

type Props = {
  works: WorkModel[];
  queued?: boolean;
  selected: WorkModel[];
  onSelectedChange: (works: WorkModel[]) => void;
  sortBy: WorkTableSort;
  sortOrder: "asc" | "desc";
  onSortChange: (sortBy: WorkTableSort, sortOrder: "asc" | "desc") => void;
  /** Role id → member names, for the role chip's tooltip. */
  roleMembers?: Map<string, string>;
  emptyState?: React.ReactNode;
};

/** The Work page table in the V49 design. */
export const BoardWorkTable: FC<Props> = ({
  works,
  queued,
  selected,
  onSelectedChange,
  sortBy,
  sortOrder,
  onSortChange,
  roleMembers,
  emptyState,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const columns = queued ? QUEUED_COLUMNS : DEFAULT_COLUMNS;
  const selectedIds = new Set(selected.map((work) => work.workId));
  const allSelected = allWorksSelected(works, selected);

  const toggleExpanded = (workId: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(workId)) next.delete(workId);
      else next.add(workId);
      return next;
    });

  const toggleSelected = (work: WorkModel) =>
    onSelectedChange(toggleSelectedWork(selected, work));

  const sortClick = (column: Column) => {
    const sort = COLUMN_SORT[column];
    if (!sort) return;
    onSortChange(sort, sortBy === sort && sortOrder === "asc" ? "desc" : "asc");
  };

  const cell = (column: Column, work: WorkModel, index: number) => {
    const isRecurring = !!work.repeatType && work.repeatType !== "Never";
    switch (column) {
      case "name":
        return (
          <td
            key={column}
            className={"min-w-[190px] max-w-[320px] px-3.5 py-[13px]"}>
            <div className={"flex min-w-0 items-center gap-[7px]"}>
              {isRecurring && (
                <span
                  title={`Recurring: ${work.repeatType}`}
                  className={
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#EDE9FF] text-[#5B3DF5]"
                  }>
                  <LuRepeat size={11} strokeWidth={2.4} />
                </span>
              )}
              {/* Same as Home's work names (Roadmap 1860, owner 2026-10-21). */}
              <span
                className={
                  "truncate text-[13.5px] font-semibold text-[#6B4EFF] hover:underline"
                }>
                {work.workName}
              </span>
            </div>
            <div className={"mt-0.5 truncate text-xs text-[#9097A8]"}>
              {subtasksText(work)}
            </div>
          </td>
        );
      case "role":
        return (
          <td key={column} className={"whitespace-nowrap px-3.5 py-[13px]"}>
            {work.roleName ? (
              <span
                title={
                  roleMembers?.get((work.roleId || "").toLowerCase()) ||
                  work.roleName
                }
                className={
                  "inline-flex items-center gap-1.5 rounded-[7px] border border-[#DDD5FA] bg-[#F0EDFF] px-[11px] py-1 text-[12.5px] font-bold text-[#5B3DF5]"
                }>
                <LuUsers size={12} strokeWidth={2} />
                {work.roleName}
              </span>
            ) : (
              <span className={"text-[13px] text-[#9097A8]"}>No role</span>
            )}
          </td>
        );
      case "type":
        return (
          <td key={column} className={classNames(TD, "whitespace-nowrap")}>
            {work.workType}
          </td>
        );
      case "contact":
        return (
          <td key={column} className={"max-w-[220px] px-3.5 py-[13px]"}>
            <button
              type={"button"}
              onClick={(event) => {
                event.stopPropagation();
                navigate(
                  navigateToContact({
                    type: work.businessContactId ? "organization" : "contact",
                    id: work.businessContactId || "",
                    customerId: work.customerId || "",
                  }),
                );
              }}
              className={"group inline-flex max-w-full items-center gap-2"}>
              <span
                className={classNames(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white",
                  work.businessContactId ? "bg-[#7E22CE]" : "bg-[#1E3A8A]",
                )}>
                {getInitialsFromFullName(work.customerName)}
              </span>
              <span
                className={
                  "truncate text-[13.5px] font-semibold text-[#3A4256] group-hover:text-[#6B4EFF] group-hover:underline"
                }>
                {work.customerName}
              </span>
            </button>
          </td>
        );
      case "status":
        return (
          <td
            key={column}
            className={"whitespace-nowrap px-3.5 py-[13px]"}
            onClick={(event) => event.stopPropagation()}>
            {work.jobStatus && (
              <StatusesDropdown
                workTypeId={work.workTypeId || ""}
                workId={work.workId || ""}
                positionType={
                  works.length - index < 4 ? undefined : "right-bottom"
                }
                triggerElement={
                  <span
                    className={
                      "inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-[#E6E8ED] bg-white px-[11px] py-[5px] text-[12.5px] font-semibold text-[#3A4256]"
                    }>
                    <span
                      className={"h-[9px] w-[9px] rounded-full"}
                      style={{
                        backgroundColor: getStatusColor(work.jobStatus || ""),
                      }}
                    />
                    {showStatus(work)}
                    <LuChevronDown
                      size={11}
                      strokeWidth={2.4}
                      className={"text-[#9097A8]"}
                    />
                  </span>
                }
              />
            )}
          </td>
        );
      case "start":
        return (
          <td key={column} className={classNames(TD, "whitespace-nowrap")}>
            {moment(work.startDate).format("MMM DD, YYYY")}
          </td>
        );
      case "due": {
        const overdue = moment().startOf("day").isAfter(moment(work.dueDate));
        return (
          <td
            key={column}
            className={classNames(
              TD,
              "whitespace-nowrap",
              overdue && "!text-[#E5484D]",
            )}>
            {moment(work.dueDate).format("MMM DD, YYYY")}
          </td>
        );
      }
      case "progress": {
        const percent = getWorkProgress({
          taskCounts: work.taskCounts || 0,
          completedTaskCounts: work.completedTaskCounts || 0,
        });
        return (
          <td key={column} className={"whitespace-nowrap px-3.5 py-[13px]"}>
            <Bar percent={percent} color={"#47DE96"} label={`${percent}%`} />
          </td>
        );
      }
      case "budget": {
        const used = getTimeBudgetProgress(work);
        const color = getBudgetColor(used);
        return (
          <td key={column} className={"whitespace-nowrap px-3.5 py-[13px]"}>
            <Bar
              percent={used}
              color={used ? color : "#E6E8ED"}
              label={`${used}%`}
              labelColor={used > 100 ? "#E5484D" : undefined}
            />
          </td>
        );
      }
      case "assignee": {
        const people = work.assigneeList || [];
        const names = people
          .map((p) => `${p.firstName} ${p.lastName}`)
          .join(", ");
        return (
          <td
            key={column}
            className={"whitespace-nowrap py-[13px] pl-3.5 pr-[18px]"}>
            <span title={names} className={"inline-flex items-center"}>
              {people.slice(0, 3).map((person, i) => (
                <span
                  key={person.assignedUserId || i}
                  className={
                    "relative flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-[0_0_0_2px_#fff]"
                  }
                  style={{
                    backgroundColor: person.color || "#1E3A8A",
                    marginLeft: i ? -7 : 0,
                  }}>
                  {getInitials(person.firstName, person.lastName)}
                </span>
              ))}
              {people.length > 3 && (
                <span className={"ml-1 text-xs font-bold text-[#6B7185]"}>
                  +{people.length - 3}
                </span>
              )}
            </span>
          </td>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className={"w-full overflow-x-auto"}>
      <table className={"w-full border-collapse"}>
        <thead>
          <tr className={"border-b border-[#ECEEF2] bg-[#FCFCFD]"}>
            <th className={"w-[26px]"} />
            <th className={"w-[34px] py-[13px] pl-3.5"}>
              <SelectBox
                checked={allSelected}
                label={allSelected ? "Unselect all" : "Select all"}
                onChange={() =>
                  onSelectedChange(toggleAllWorks(works, selected))
                }
              />
            </th>
            {columns.map((column) => {
              const sort = COLUMN_SORT[column];
              const isSorted = !!sort && sortBy === sort;
              return (
                <th
                  key={column}
                  onClick={() => sortClick(column)}
                  className={classNames(
                    TH,
                    isSorted ? "text-[#6B4EFF]" : "text-[#9097A8]",
                    sort && "cursor-pointer select-none",
                  )}>
                  <span className={"inline-flex items-center gap-1"}>
                    {COLUMN_TITLES[column]}
                    {isSorted &&
                      (sortOrder === "asc" ? (
                        <LuArrowUp size={11} strokeWidth={2.4} />
                      ) : (
                        <LuArrowDown size={11} strokeWidth={2.4} />
                      ))}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {works.map((work, index) => {
            const isOpen = expanded.has(work.workId);
            return (
              <Fragment key={work.workId}>
                <tr
                  onClick={() => navigate(`/work/${work.workId}`)}
                  className={classNames(
                    "cursor-pointer border-b border-[#F0F1F4] hover:bg-[#FAFAFC]",
                    selectedIds.has(work.workId) && "bg-[#FAF9FF]",
                  )}>
                  <td className={"py-[13px] pl-2"}>
                    <button
                      type={"button"}
                      aria-label={isOpen ? "Hide subtasks" : "Show subtasks"}
                      aria-expanded={isOpen}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleExpanded(work.workId);
                      }}
                      className={
                        "flex h-5 w-5 items-center justify-center text-[#B4B9C6] hover:text-[#6B4EFF]"
                      }>
                      <LuChevronRight
                        size={14}
                        strokeWidth={2.4}
                        className={classNames(
                          "transition-transform",
                          isOpen && "rotate-90",
                        )}
                      />
                    </button>
                  </td>
                  <td
                    className={"cursor-default py-[13px] pl-3.5 pr-2"}
                    onClick={(event) => {
                      // The whole cell selects, so a near miss doesn't open the work.
                      event.stopPropagation();
                      toggleSelected(work);
                    }}>
                    <SelectBox
                      checked={selectedIds.has(work.workId)}
                      onChange={() => toggleSelected(work)}
                    />
                  </td>
                  {columns.map((column) => cell(column, work, index))}
                </tr>
                {isOpen && (
                  <tr className={"border-b border-[#F0F1F4] bg-[#FCFCFD]"}>
                    <td colSpan={columns.length + 2} className={"px-4 py-3"}>
                      <WorkTasksExpandedTable
                        workId={work.workId}
                        workTypeId={work.workTypeId}
                        workStatusId={work.jobStatusId}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {!works.length && emptyState}
    </div>
  );
};
